"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Measures the referenced element and reports its height back in pixels, so a
 * container can transition between content heights instead of snapping.
 *
 * `isTurning` exists to gate the CSS transition. Height also changes whenever
 * text reflows — a window resize, a font swap — and animating those would
 * leave the box lagging behind its content. Callers flag a deliberate change
 * with `startTurn` and clear it with `endTurn` once the transition finishes.
 */
export function useAnimatedHeight<TElement extends HTMLElement>() {
  const ref = useRef<TElement>(null);
  const [height, setHeight] = useState<number>();
  const [isTurning, setIsTurning] = useState(false);

  useEffect(() => {
    const element = ref.current;

    // Unsupported or not yet mounted: fall back to the natural auto height.
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      const measured = entry.contentRect.height;

      // Never pin a zero height, or the box collapses mid-layout.
      if (measured > 0) {
        setHeight(measured);
      }
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const startTurn = useCallback(() => setIsTurning(true), []);
  const endTurn = useCallback(() => setIsTurning(false), []);

  return { endTurn, height, isTurning, ref, startTurn };
}
